import { SalesforceIntegrationId } from 'module/api/integrations/salesforce/types';
import { Loading } from 'module/ui/components/atoms/loading';
import { useAutomationContact } from 'hooks/integrations/use-automation-contact';
import { useSalesForceContactFieldSyncDetailQuery } from 'queries/salesforce';
import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { BlockList } from 'components/organisms/signal/detail/block-list/list';
import { Checkbox } from '@rocketlane/ui';
import { ContactFieldSyncModal } from '../../contact-field-sync-model';
import { ActionDrawer } from '../../create/action-drawer';
import { useAutomationContext } from '../../create/automation-context';
import { RenderBlockDetails } from '../../create/blocks/shared/render-block-details';

interface AutomationContactBlockProps {
  isContactSelected?: boolean;
}

export const AutomationContactEditBlock: FC<AutomationContactBlockProps> = ({
  isContactSelected = false,
}) => {
  const { integrationId } = useParams<SalesforceIntegrationId>();
  const { automationDetails, setAutomationDetails } = useAutomationContext();

  const {
    allFields,
    isLoading,
    isContactFieldsLoading,
    actionDrawerVisible,
    setActionDrawerVisible,
    fieldsKey,
    salesForceActionsType,
    value,
    onSave,
    handleContactObjectChange,
    isSyncConfigured,
    missingMandatoryFields,
  } = useAutomationContact({
    integrationId: integrationId || '',
    isContactSelected,
    isEditMode: true,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const autoFetchNewContacts =
    automationDetails?.generateAutomationForFutureInserts ?? false;
  const hasContactObjectSelected = Boolean(automationDetails?.sourceResourceType);

  // Fetch detailed field sync config when modal opens
  const { data: fieldSyncDetailConfig, isLoading: isLoadingDetail } =
    useSalesForceContactFieldSyncDetailQuery(
      parseInt(integrationId || '0'),
      automationDetails?.sourceResourceType || 'Contact',
      {
        enabled: modalVisible && !!automationDetails?.sourceResourceType,
      },
    );

  const handleSyncField = () => {
    // API call is handled inside the modal component
    // This callback is called after successful save
    // Refresh the validation status
    setModalVisible(false);
  };

  if (!isContactSelected) {
    return null;
  }

  const isFieldDisabled = !value || isLoading;

  return (
    <>
      <BlockList.Item
        disabled={isFieldDisabled}
        onClick={() => setActionDrawerVisible(true)}
      >
        <BlockList.Item.Label data-required="true">
          <BlockList.Item.Label.Text>
            <FormattedMessage
              id={`settings.integrations.create.automation.${salesForceActionsType?.toLowerCase()}.label`}
            />
          </BlockList.Item.Label.Text>
        </BlockList.Item.Label>
        <BlockList.Item.Content>
          {isLoading ? (
            <Loading />
          ) : (
            <RenderBlockDetails
              actions={automationDetails?.[fieldsKey] ?? {}}
              fields={allFields}
            />
          )}
        </BlockList.Item.Content>
      </BlockList.Item>

      <BlockList.Item actionIcon={false}>
        <div className="flex flex-col flex-1">
          <div className="flex flex-row gap-3 px-6 py-3 items-start">
            <Checkbox
              checked={autoFetchNewContacts}
              disabled={isFieldDisabled}
              style={{ marginTop: 3 }}
              onCheckedChange={(checked) =>
                setAutomationDetails({
                  generateAutomationForFutureInserts: checked,
                })
              }
            />
            <div className="flex flex-col">
              <div className="text-[14px] leading-5 text-[#161616] font-medium">
                <FormattedMessage id="settings.integrations.create.automation.contact.auto_fetch.label" />
              </div>
              <div className="font-normal text-[12px] leading-4 text-[#525252]">
                <FormattedMessage id="settings.integrations.create.automation.contact.auto_fetch.description" />
              </div>
            </div>
          </div>
        </div>
      </BlockList.Item>

      <ContactFieldSyncModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSyncField={handleSyncField}
        contactObjectName={automationDetails?.sourceResourceType || 'Contact'}
        integrationId={parseInt(integrationId || '0')}
        isLoading={isLoadingDetail}
        fieldMappings={
          missingMandatoryFields?.map((field) => {
            // Find existing mapping from detail config if available
            const existingMapping = fieldSyncDetailConfig?.fieldMappings?.find(
              (fm) => fm.nativeFieldName === field.fieldName,
            );

            return {
              salesforceField: existingMapping?.externalFieldLabel || null,
              rocketlaneField: field.fieldLabel || null,
              nativeFieldName: field.fieldName || undefined,
            };
          }) || []
        }
        fieldSyncDetailConfig={fieldSyncDetailConfig}
      />

      <ActionDrawer
        label="user"
        fields={allFields ?? []}
        fieldsStatus={isLoading ? 'pending' : 'success'}
        visible={actionDrawerVisible}
        value={automationDetails?.[fieldsKey] ?? undefined}
        handleModalVisibility={setActionDrawerVisible}
        onSave={onSave}
        onClose={() => setActionDrawerVisible(false)}
        onContactObjectChange={handleContactObjectChange}
        isContactFieldsLoading={
          isContactFieldsLoading && hasContactObjectSelected
        }
        isSyncConfigured={isSyncConfigured}
        onShowSyncModal={() => setModalVisible(true)}
      />
    </>
  );
};
